import axios from 'axios';
import { getApiBase, getAuthHeaders } from './api';

export async function getAirlineByCode(code) {
  if (!code) return null;
  const upperCode = String(code).trim().toUpperCase();
  const apiBase = getApiBase();
  try {
    const res = await axios.get(`${apiBase}/airlines/${upperCode}`, { headers: getAuthHeaders() });
    return res.data || null;
  } catch (e) {
    return null;
  }
}

/**
 * Look up an airline by name and return its details including the 2-letter IATA code
 * Uses the search API to find the best match
 * @param {string} name - The airline name to search for (e.g., "Frontier Airlines")
 * @returns {Promise<Object|null>} Airline object with code and name, or null if not found
 */
export async function getAirlineByName(name) {
  if (!name) return null;

  const trimmedName = String(name).trim();

  // If it looks like a 2-letter code already, use getAirlineByCode
  if (trimmedName.length <= 3 && /^[A-Z0-9]+$/i.test(trimmedName)) {
    return getAirlineByCode(trimmedName);
  }

  try {
    // Search for the airline by name - request more results for better matching
    const results = await searchAirlines(trimmedName, 15);

    if (results && results.length > 0) {
      const lowerName = trimmedName.toLowerCase();

      // Priority 1: Exact name match
      const exactMatch = results.find(
        a => a.name && a.name.toLowerCase() === lowerName
      );
      if (exactMatch) {
        return exactMatch;
      }

      // Priority 2: Exact name match with "Airlines" suffix (e.g., "Southwest" -> "Southwest Airlines")
      const withAirlinesSuffix = results.find(
        a => a.name && a.name.toLowerCase() === `${lowerName} airlines`
      );
      if (withAirlinesSuffix) {
        return withAirlinesSuffix;
      }

      // Priority 3: Name starts with the search term (prefix match)
      // This ensures "Southwest Airlines" matches before "China Southwest Airlines"
      const prefixMatch = results.find(
        a => a.name && a.name.toLowerCase().startsWith(lowerName)
      );
      if (prefixMatch) {
        return prefixMatch;
      }

      // Priority 4: Name starts with search term followed by space
      const wordPrefixMatch = results.find(
        a => a.name && a.name.toLowerCase().startsWith(`${lowerName} `)
      );
      if (wordPrefixMatch) {
        return wordPrefixMatch;
      }

      // Fallback: return the first result (backend should have ranked them)
      return results[0];
    }

    return null;
  } catch (e) {
    console.error('Error looking up airline by name:', e);
    return null;
  }
}

export async function searchAirlines(query, limit = 10) {
  if (!query) return [];
  const apiBase = getApiBase();
  // Backend expects 'query' parameter, not 'q'
  const url = `${apiBase}/airlines/search?query=${encodeURIComponent(query)}&limit=${limit}`;
  try {
    const res = await axios.get(url, {
      headers: getAuthHeaders()
    });
    return res.data || [];
  } catch (e) {
    console.error('Airline search error:', e.message);
    return [];
  }
}

export function formatFlightNumber(flightNumber, airlineCode) {
  if (!flightNumber) return '';

  // Remove the airline code prefix if it exists
  let number = String(flightNumber).trim();
  if (airlineCode && number.toUpperCase().startsWith(airlineCode.toUpperCase())) {
    number = number.slice(airlineCode.length);
  }

  // Add the airline code prefix back if we have it
  if (airlineCode) {
    return `${airlineCode.toUpperCase()}${number}`;
  }

  return number;
}

/**
 * Extract just the numerical flight number, removing any airline code prefix
 * @param {string} flightNumber - The flight number which may include airline code (e.g., "AA1970" or "1970" or "AA 1970")
 * @param {string} airlineCode - The airline code to strip if present (e.g., "AA")
 * @returns {string} Just the numerical flight number (e.g., "1970")
 */
export function extractFlightNumber(flightNumber, airlineCode) {
  if (!flightNumber) return '';

  let number = String(flightNumber).trim();

  // Remove the airline code prefix if it exists (with or without space)
  if (airlineCode) {
    const codeUpper = airlineCode.toUpperCase();
    const numberUpper = number.toUpperCase();
    if (numberUpper.startsWith(codeUpper + ' ')) {
      number = number.slice(airlineCode.length + 1);
    } else if (numberUpper.startsWith(codeUpper)) {
      number = number.slice(airlineCode.length);
    }
  }

  // Also try to detect and remove any 2-letter prefix that looks like an airline code
  // This handles cases where the data has the code embedded but we don't have the airlineCode param
  // Handles both "J222" and "J2 22" formats
  const matchWithSpace = number.match(/^([A-Z0-9]{2})\s+(\d+.*)$/i);
  if (matchWithSpace) {
    number = matchWithSpace[2];
  } else {
    const matchNoSpace = number.match(/^([A-Z]{2})(\d+.*)$/i);
    if (matchNoSpace) {
      number = matchNoSpace[2];
    }
  }

  return number.trim();
}

/**
 * Parse a combined flight number string to extract airline code and flight number
 * Handles formats like: "J2 22", "AA1970", "EK 205", "UA 123"
 * @param {string} flightNumber - The combined flight number string
 * @returns {Object} { airlineCode: string, flightNumber: string }
 */
export function parseFlightNumber(flightNumber) {
  if (!flightNumber) return { airlineCode: '', flightNumber: '' };

  const str = String(flightNumber).trim();

  // Try to match format with space: "J2 22", "AA 1970"
  const matchWithSpace = str.match(/^([A-Z0-9]{2})\s+(\d+.*)$/i);
  if (matchWithSpace) {
    return {
      airlineCode: matchWithSpace[1].toUpperCase(),
      flightNumber: matchWithSpace[2].trim()
    };
  }

  // Try to match format without space: "AA1970", "EK205"
  const matchNoSpace = str.match(/^([A-Z]{2})(\d+.*)$/i);
  if (matchNoSpace) {
    return {
      airlineCode: matchNoSpace[1].toUpperCase(),
      flightNumber: matchNoSpace[2].trim()
    };
  }

  // No airline code found, return as-is
  return { airlineCode: '', flightNumber: str };
}